import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import {
  Users, ShieldCheck, FileText, Mail, Briefcase, BarChart3, Search,
  RefreshCw, Ban, ArrowUpRight, ChevronRight, Plus, Trash2, Edit3, X,
  AlertCircle, EyeOff, Eye, Lock, DollarSign, Save,
} from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview",          Icon: BarChart3 },
  { key: "users",    label: "Users",             Icon: Users },
  { key: "sends",    label: "Outreach moderation", Icon: Mail },
  { key: "kits",     label: "Public kits",       Icon: FileText },
  { key: "legal",    label: "Legal audit",       Icon: ShieldCheck },
  { key: "brands",   label: "Brand directory",   Icon: Briefcase },
  { key: "pricing",  label: "Pricing",           Icon: DollarSign },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("overview");

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) {
    return (
      <DashboardLayout>
        <div className="p-6 md:p-12" data-testid="admin-forbidden">
          <div className="surface p-10 max-w-xl">
            <Lock className="w-10 h-10 text-volt mb-4" />
            <h1 className="font-heading text-3xl uppercase tracking-tight mb-2">Admin only</h1>
            <p className="text-ink-muted text-sm">This area is restricted to PITCHD administrators.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 md:p-12" data-testid="admin-root">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <div className="label-eyebrow">Admin console</div>
            <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2">Mission control</h1>
          </div>
          <div className="flex items-center gap-3" data-testid="admin-header-row">
            <span className="text-xs font-mono text-ink-muted px-2 py-1 border border-volt/40 text-volt" data-testid="admin-badge">
              ADMIN · {user.email}
            </span>
            <a href="/app/admin/moderation" data-testid="admin-moderation-link"
              className="text-xs uppercase tracking-widest border border-volt/50 text-volt hover:bg-volt hover:text-black px-3 py-1.5 transition-colors">
              Moderation →
            </a>
          </div>
        </div>

        <nav className="flex flex-wrap gap-1 border-b border-line mb-8" data-testid="admin-tabs">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`px-4 py-3 text-xs uppercase tracking-widest border-b-2 inline-flex items-center gap-2 transition-colors ${
                tab === t.key
                  ? "border-volt text-volt"
                  : "border-transparent text-ink-muted hover:text-white"
              }`}
            >
              <t.Icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
        </nav>

        {tab === "overview" && <OverviewTab />}
        {tab === "users"    && <UsersTab />}
        {tab === "sends"    && <SendsTab />}
        {tab === "kits"     && <KitsTab />}
        {tab === "legal"    && <LegalTab />}
        {tab === "brands"   && <BrandsTab />}
        {tab === "pricing"  && <PricingTab />}
      </div>
    </DashboardLayout>
  );
}

/* ---------------- Overview ---------------- */
function OverviewTab() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/overview").then(({data}) => setD(data));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="text-sm text-ink-muted">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <Section title="Revenue">
        <div className="grid sm:grid-cols-4 gap-3">
          <Stat label="MRR" value={`£${d.revenue.mrr_gbp}`} accent testid="ov-mrr" />
          <Stat label="ARR (run-rate)" value={`£${d.revenue.arr_gbp}`} testid="ov-arr" />
          <Stat label="Pro subs" value={d.revenue.pro_count} testid="ov-pro" />
          <Stat label="Agency subs" value={d.revenue.agency_count} testid="ov-agency" />
        </div>
      </Section>
      <Section title="Users">
        <div className="grid sm:grid-cols-5 gap-3">
          <Stat label="Total" value={d.users.total} testid="ov-users-total" />
          <Stat label="New 24h" value={d.users.new_24h} testid="ov-users-24h" />
          <Stat label="New 7d" value={d.users.new_7d} testid="ov-users-7d" />
          <Stat label="New 30d" value={d.users.new_30d} testid="ov-users-30d" />
          <Stat label="Suspended" value={d.users.suspended} danger={d.users.suspended > 0} testid="ov-suspended" />
        </div>
      </Section>
      {d.personas && <PersonaSplit personas={d.personas} />}
      <Section title="Plan mix">
        <div className="grid sm:grid-cols-3 gap-3">
          <Stat label="Free" value={d.plans.free} testid="ov-plan-free" />
          <Stat label="Pro" value={d.plans.pro} testid="ov-plan-pro" />
          <Stat label="Agency" value={d.plans.agency} testid="ov-plan-agency" />
        </div>
      </Section>
      <Section title="Platform activity">
        <div className="grid sm:grid-cols-5 gap-3">
          <Stat label="Brands" value={d.activity.total_brands} testid="ov-brands" />
          <Stat label="Media kits" value={d.activity.total_media_kits} testid="ov-kits" />
          <Stat label="Sends (all-time)" value={d.activity.sends_total} testid="ov-sends-all" />
          <Stat label="Sends (30d)" value={d.activity.sends_30d} testid="ov-sends-30d" />
          <Stat label="Bounced (30d)" value={d.activity.bounced_30d} danger={d.activity.bounced_30d > 0} testid="ov-bounced-30d" />
        </div>
      </Section>
      <p className="text-[10px] text-ink-dim font-mono">Generated at {d.generated_at}</p>
    </div>
  );
}

/* ---------------- Users ---------------- */
// Small colour-coded pill so admins can scan account type at a glance.
// Athletes get the dominant volt fill (they're the core persona); brands /
// agents / clubs are outlined in neutral tones so the screen doesn't look
// like a rainbow.
const ACCOUNT_TYPE_STYLES = {
  athlete: "bg-volt/15 border-volt/50 text-volt",
  brand:   "border-blue-400/40 text-blue-300",
  agent:   "border-purple-400/40 text-purple-300",
  club:    "border-orange-400/40 text-orange-300",
};
// Same hues, used by the persona-split bar so the two views feel consistent.
const PERSONA_BAR_FILL = {
  athlete: "bg-volt",
  brand:   "bg-blue-400",
  agent:   "bg-purple-400",
  club:    "bg-orange-400",
};
const PERSONA_LABEL = {
  athlete: "Athletes",
  brand:   "Brands",
  agent:   "Agents",
  club:    "Clubs",
};
function AccountTypeChip({ type }) {
  const cls = ACCOUNT_TYPE_STYLES[type] || "border-line text-ink-muted";
  return (
    <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-widest border ${cls}`}
          data-testid={`account-type-${type || "unknown"}`}>
      {type || "—"}
    </span>
  );
}

// Persona split block for the admin Overview tab.
// Renders:
//   1. Stacked horizontal bar showing the all-time persona mix (athlete /
//      brand / agent / club) with inline % labels.
//   2. Four rows below — one per persona — showing total count, 30d new,
//      previous 30d new, and a trend arrow + % delta. Lets the founder spot
//      "brand signups now 3x faster than last month" without exporting a CSV.
function PersonaSplit({ personas }) {
  const t = personas.total || {};
  const cur = personas.new_30d || {};
  const prev = personas.prev_30d || {};
  const totalAll = (t.athlete || 0) + (t.brand || 0) + (t.agent || 0) + (t.club || 0);
  const pct = (n) => totalAll === 0 ? 0 : Math.round((n / totalAll) * 100);
  const trend = (now, before) => {
    if (before === 0 && now === 0) return { dir: "flat", text: "—" };
    if (before === 0) return { dir: "up", text: `new` };
    const delta = ((now - before) / before) * 100;
    if (Math.abs(delta) < 1) return { dir: "flat", text: "0%" };
    const sign = delta > 0 ? "+" : "";
    return { dir: delta > 0 ? "up" : "down", text: `${sign}${Math.round(delta)}%` };
  };
  return (
    <Section title="Persona mix · last 30 days vs prior 30 days">
      <div className="surface p-5 space-y-5" data-testid="ov-persona-split">
        {/* Stacked bar */}
        <div className="flex h-3 w-full overflow-hidden border border-line" data-testid="ov-persona-bar">
          {["athlete","brand","agent","club"].map(k => {
            const w = pct(t[k] || 0);
            return w > 0 ? (
              <div key={k} className={PERSONA_BAR_FILL[k]} style={{ width: `${w}%` }}
                   title={`${PERSONA_LABEL[k]}: ${w}%`}
                   data-testid={`ov-persona-bar-${k}`} />
            ) : null;
          })}
        </div>
        {/* Per-persona rows */}
        <div className="space-y-2.5">
          {["athlete","brand","agent","club"].map(k => {
            const tr = trend(cur[k] || 0, prev[k] || 0);
            const arrow = tr.dir === "up" ? "↑" : tr.dir === "down" ? "↓" : "→";
            const arrowColor = tr.dir === "up" ? "text-volt" : tr.dir === "down" ? "text-[#FF3B30]" : "text-ink-dim";
            return (
              <div key={k} className="flex items-center gap-3 text-sm" data-testid={`ov-persona-row-${k}`}>
                <div className={`w-2.5 h-2.5 ${PERSONA_BAR_FILL[k]} flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-medium">{PERSONA_LABEL[k]}</span>
                    <span className="text-ink-muted text-xs">{pct(t[k] || 0)}%</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-white font-mono" data-testid={`ov-persona-total-${k}`}>{t[k] || 0}</div>
                  <div className="text-[10px] text-ink-dim uppercase tracking-widest">all-time</div>
                </div>
                <div className="text-right w-20">
                  <div className="text-white font-mono" data-testid={`ov-persona-30d-${k}`}>{cur[k] || 0}</div>
                  <div className="text-[10px] text-ink-dim uppercase tracking-widest">30d</div>
                </div>
                <div className={`text-right w-16 font-mono ${arrowColor}`} data-testid={`ov-persona-trend-${k}`}>
                  {arrow} {tr.text}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

// Relative time helper used by the Last seen column. Returns short forms
// like "2m ago", "3h ago", "5d ago", "1mo ago", or "Never" for missing values.
// Deliberately small + dependency-free; the Users table re-renders often
// (filter changes, deletions), so keep this O(1) per row.
function relTime(iso) {
  if (!iso) return "Never";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const s = Math.floor(ms / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [dormantOnly, setDormantOnly] = useState(false);
  const [sortBy, setSortBy] = useState("created_desc");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (planFilter) params.set("plan", planFilter);
    if (typeFilter) params.set("account_type", typeFilter);
    if (dormantOnly) params.set("dormant", "true");
    if (sortBy && sortBy !== "created_desc") params.set("sort", sortBy);
    api.get(`/admin/users?${params}`).then(({data}) => { setUsers(data); setLoading(false); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  // Auto-apply when any filter / sort changes so admins never have to manually click Refresh.
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [planFilter, typeFilter, dormantOnly, sortBy]);

  const save = async (id, patch) => {
    await api.patch(`/admin/users/${id}`, patch);
    load();
    setEditing(null);
  };

  const remove = async (id) => {
    await api.delete(`/admin/users/${id}`);
    load();
    setEditing(null);
  };

  return (
    <div className="space-y-4" data-testid="admin-users">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-ink-muted absolute left-3 top-3" />
          <input
            value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load()}
            placeholder="Search by email"
            className="w-full bg-bg border border-line focus:border-volt outline-none pl-9 pr-3 py-2.5 text-white text-sm"
            data-testid="users-search-input"
          />
        </div>
        <select value={typeFilter} onChange={e=>{setTypeFilter(e.target.value);}} className="bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" data-testid="users-type-filter">
          <option value="">All types</option>
          <option value="athlete">Athlete</option>
          <option value="brand">Brand</option>
          <option value="agent">Agent</option>
          <option value="club">Club</option>
        </select>
        <select value={planFilter} onChange={e=>{setPlanFilter(e.target.value);}} className="bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" data-testid="users-plan-filter">
          <option value="">All plans</option>
          <option value="free">Free</option>
          <option value="pro">Pro</option>
          <option value="agency">Agency</option>
          <option value="brand_lite">Brand Lite</option>
          <option value="brand_pro">Brand Pro</option>
        </select>
        <select value={sortBy} onChange={e=>setSortBy(e.target.value)} className="bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" data-testid="users-sort-select">
          <option value="created_desc">Newest signups</option>
          <option value="created_asc">Oldest signups</option>
          <option value="recent">Recently active</option>
          <option value="dormant_first">Longest dormant</option>
        </select>
        <button
          onClick={() => setDormantOnly(v => !v)}
          aria-pressed={dormantOnly}
          data-testid="users-dormant-toggle"
          className={`text-xs uppercase tracking-widest border px-3 py-2.5 transition-colors ${
            dormantOnly
              ? "bg-volt text-black border-volt font-semibold"
              : "bg-bg text-ink-muted border-line hover:text-white"
          }`}>
          Dormant &gt;30d {dormantOnly && "✓"}
        </button>
        <button onClick={load} className="btn-ghost-line text-xs inline-flex items-center gap-2" data-testid="users-refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="surface overflow-x-auto" data-testid="users-table">
        <table className="w-full text-xs">
          <thead className="border-b border-line">
            <tr className="text-left text-ink-muted uppercase tracking-widest">
              <th className="px-4 py-3 font-normal">Email</th>
              <th className="px-4 py-3 font-normal">Type</th>
              <th className="px-4 py-3 font-normal">Plan</th>
              <th className="px-4 py-3 font-normal text-right">Brands</th>
              <th className="px-4 py-3 font-normal text-right">Sends</th>
              <th className="px-4 py-3 font-normal text-right">Kits</th>
              <th className="px-4 py-3 font-normal">Last seen</th>
              <th className="px-4 py-3 font-normal">Status</th>
              <th className="px-4 py-3 font-normal text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {users.map(u => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3">
                  <div className="text-white">{u.email}</div>
                  <div className="text-ink-dim text-[10px] font-mono">{u.id}</div>
                </td>
                <td className="px-4 py-3">
                  <AccountTypeChip type={u.account_type} />
                </td>
                <td className="px-4 py-3 uppercase tracking-widest text-volt">{u.plan || "free"}</td>
                <td className="px-4 py-3 text-right font-mono">{u.brand_count}</td>
                <td className="px-4 py-3 text-right font-mono">{u.send_count}</td>
                <td className="px-4 py-3 text-right font-mono">{u.kit_count}</td>
                <td className="px-4 py-3" data-testid={`user-last-seen-${u.id}`}
                    title={u.last_login_at || "Never logged in"}>
                  <span className={u.last_login_at ? "text-ink-muted" : "text-ink-dim italic"}>
                    {relTime(u.last_login_at)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {u.suspended && <span className="px-2 py-1 text-[10px] uppercase border border-[#FF3B30]/40 text-[#FF3B30]">Suspended</span>}
                  {u.is_admin && <span className="px-2 py-1 text-[10px] uppercase border border-volt/40 text-volt ml-1">Admin</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing(u)} className="text-volt text-[10px] uppercase tracking-widest hover:underline" data-testid={`user-edit-${u.id}`}>
                    Manage
                  </button>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan="9" className="px-4 py-8 text-center text-ink-muted">No users found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <UserEditModal user={editing} onSave={save} onDelete={remove} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

function UserEditModal({ user, onSave, onDelete, onClose }) {
  const [plan, setPlan] = useState(user.plan || "free");
  const [suspended, setSuspended] = useState(!!user.suspended);
  const [isAdmin, setIsAdmin] = useState(!!user.is_admin);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [deleting, setDeleting] = useState(false);
  const canDelete = !user.is_admin;
  const confirmMatches = confirmEmail.trim().toLowerCase() === (user.email || "").toLowerCase();
  const submit = async () => {
    setSaving(true); setErr("");
    try { await onSave(user.id, { plan, suspended, is_admin: isAdmin }); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  const runDelete = async () => {
    if (!confirmMatches) return;
    setDeleting(true); setErr("");
    try { await onDelete(user.id); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); setDeleting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="user-edit-modal">
      <div className="bg-bg-surface border border-volt w-full max-w-md">
        <div className="px-6 py-4 border-b border-line flex justify-between items-center">
          <div>
            <div className="label-eyebrow">Manage user</div>
            <h2 className="font-heading text-xl uppercase mt-1 truncate max-w-[280px]">{user.email}</h2>
          </div>
          <button onClick={onClose} data-testid="user-edit-close"><X className="w-5 h-5 text-ink-muted hover:text-white" /></button>
        </div>
        <div className="p-6 space-y-5">
          <label className="block">
            <div className="label-eyebrow mb-2">Plan</div>
            <select value={plan} onChange={e=>setPlan(e.target.value)} className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white" data-testid="user-edit-plan">
              <option value="free">Free</option>
              <option value="pro">Pro</option>
              <option value="agency">Agency</option>
              <option value="brand_lite">Brand Lite</option>
              <option value="brand_pro">Brand Pro</option>
            </select>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={suspended} onChange={e=>setSuspended(e.target.checked)} className="w-4 h-4 accent-[#FF3B30]" data-testid="user-edit-suspended" />
            <span className="text-sm text-white">Suspend account <span className="text-ink-muted text-xs">(blocks all access)</span></span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={isAdmin} onChange={e=>setIsAdmin(e.target.checked)} className="w-4 h-4 accent-volt" data-testid="user-edit-admin" />
            <span className="text-sm text-white">Grant admin access</span>
          </label>
          {err && <div className="text-xs text-[#FF3B30]">{err}</div>}

          {/* Danger zone */}
          {canDelete && (
            <div className="border-t border-line pt-5">
              <div className="label-eyebrow text-[#FF3B30] mb-2">Danger zone</div>
              {!showDelete && (
                <button onClick={() => setShowDelete(true)} data-testid="user-edit-delete-open"
                  className="text-xs uppercase tracking-widest border border-[#FF3B30]/40 text-[#FF3B30] px-3 py-2 hover:bg-[#FF3B30]/10 inline-flex items-center gap-2">
                  <Trash2 className="w-3 h-3" /> Delete this user
                </button>
              )}
              {showDelete && (
                <div className="space-y-3 bg-[#FF3B30]/10 border border-[#FF3B30]/30 p-4">
                  <div className="text-xs text-white leading-relaxed">
                    This permanently deletes <b>{user.email}</b> and ALL their data — media kits, outreach, brands,
                    conversations, kit views, payment history, social connections. Stripe subscription will be
                    cancelled. <span className="text-[#FF3B30] font-medium">This action cannot be undone.</span>
                  </div>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-widest text-ink-muted mb-1.5">
                      Type the user's email to confirm
                    </div>
                    <input type="text" value={confirmEmail} onChange={e=>setConfirmEmail(e.target.value)}
                      placeholder={user.email} autoComplete="off"
                      className="w-full bg-bg border border-line focus:border-[#FF3B30] outline-none px-3 py-2 text-white text-sm font-mono"
                      data-testid="user-edit-delete-confirm-email" />
                  </label>
                  <div className="flex gap-2">
                    <button onClick={() => { setShowDelete(false); setConfirmEmail(""); }}
                      className="btn-ghost-line text-xs flex-1" data-testid="user-edit-delete-cancel">Cancel</button>
                    <button onClick={runDelete} disabled={!confirmMatches || deleting}
                      data-testid="user-edit-delete-confirm"
                      className="flex-1 px-3 py-2 text-xs uppercase tracking-widest bg-[#FF3B30] text-white hover:bg-[#E52E25] disabled:opacity-30 disabled:cursor-not-allowed">
                      {deleting ? "Deleting…" : "Permanently delete"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-line flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost-line">Cancel</button>
          <button onClick={submit} disabled={saving} className="btn-volt disabled:opacity-50" data-testid="user-edit-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Outreach moderation ---------------- */
function SendsTab() {
  const [sends, setSends] = useState([]);
  const [status, setStatus] = useState("");
  const load = () => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    api.get(`/admin/sends?${p}`).then(({data}) => setSends(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  return (
    <div className="space-y-4" data-testid="admin-sends">
      <div className="flex flex-wrap gap-2 items-center">
        <select value={status} onChange={e=>setStatus(e.target.value)} className="bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" data-testid="sends-status-filter">
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="delivered">Delivered</option>
          <option value="opened">Opened</option>
          <option value="clicked">Clicked</option>
          <option value="bounced">Bounced</option>
          <option value="complained">Complained</option>
          <option value="failed">Failed</option>
        </select>
        <button onClick={load} className="btn-ghost-line text-xs inline-flex items-center gap-2" data-testid="sends-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
        <span className="text-xs text-ink-muted ml-auto">{sends.length} sends</span>
      </div>

      <div className="surface overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="border-b border-line">
            <tr className="text-left text-ink-muted uppercase tracking-widest">
              <th className="px-4 py-3 font-normal">When</th>
              <th className="px-4 py-3 font-normal">Sender</th>
              <th className="px-4 py-3 font-normal">Brand</th>
              <th className="px-4 py-3 font-normal">To</th>
              <th className="px-4 py-3 font-normal">Subject</th>
              <th className="px-4 py-3 font-normal">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {sends.map(s => (
              <tr key={s.id} data-testid={`send-row-${s.id}`}>
                <td className="px-4 py-3 font-mono text-[10px] text-ink-muted">{formatDate(s.created_at)}</td>
                <td className="px-4 py-3"><div className="text-white">{s.sender_name || "—"}</div><div className="text-ink-dim text-[10px]">{s.sender_email}</div></td>
                <td className="px-4 py-3 text-white">{s.brand_name || "—"}</td>
                <td className="px-4 py-3 text-ink-muted">{s.recipient}</td>
                <td className="px-4 py-3 text-white truncate max-w-[200px]">{s.subject || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-[10px] uppercase border ${statusCls(s.status)}`}>{s.status}</span>
                </td>
              </tr>
            ))}
            {sends.length === 0 && <tr><td colSpan="6" className="px-4 py-8 text-center text-ink-muted">No sends.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Public Kits moderation ---------------- */
function KitsTab() {
  const [kits, setKits] = useState([]);
  const [hiddenOnly, setHiddenOnly] = useState(false);
  const load = () => api.get(`/admin/media-kits?hidden_only=${hiddenOnly}`).then(({data}) => setKits(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hiddenOnly]);
  const toggle = async (k) => {
    await api.patch(`/admin/media-kits/${k.id}`, { is_hidden: !k.is_hidden });
    load();
  };

  return (
    <div className="space-y-4" data-testid="admin-kits">
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-ink-muted cursor-pointer">
          <input type="checkbox" checked={hiddenOnly} onChange={e=>setHiddenOnly(e.target.checked)} className="accent-volt" data-testid="kits-hidden-filter" />
          Show only hidden
        </label>
        <span className="text-xs text-ink-muted ml-auto">{kits.length} public kits</span>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {kits.map(k => (
          <div key={k.id} className="surface p-5" data-testid={`kit-${k.id}`}>
            <div className="flex justify-between items-start gap-3 mb-2">
              <div>
                <div className="font-heading text-base uppercase tracking-tight">{k.athlete_name || "Unknown athlete"}</div>
                <div className="text-xs text-ink-muted">{k.athlete_email}</div>
              </div>
              {k.is_hidden && <span className="px-2 py-1 text-[10px] uppercase border border-[#FF3B30]/40 text-[#FF3B30]">Hidden</span>}
            </div>
            <div className="text-xs text-ink-muted font-mono">/kit/{k.share_slug}</div>
            <div className="text-[10px] text-ink-dim mt-1">Created {formatDate(k.created_at)}</div>
            <div className="mt-4 flex gap-2">
              <a href={`/kit/${k.share_slug}`} target="_blank" rel="noreferrer" className="btn-ghost-line text-xs inline-flex items-center gap-1" data-testid={`kit-view-${k.id}`}>
                <ArrowUpRight className="w-3 h-3" /> View
              </a>
              <button onClick={() => toggle(k)} className="btn-ghost-line text-xs inline-flex items-center gap-1" data-testid={`kit-toggle-${k.id}`}>
                {k.is_hidden ? <><Eye className="w-3 h-3" /> Unhide</> : <><EyeOff className="w-3 h-3" /> Hide from public</>}
              </button>
            </div>
          </div>
        ))}
        {kits.length === 0 && <div className="surface p-8 text-center text-ink-muted text-sm">No public media kits.</div>}
      </div>
    </div>
  );
}

/* ---------------- Legal audit ---------------- */
function LegalTab() {
  const [rows, setRows] = useState([]);
  const [docType, setDocType] = useState("");
  const [userId, setUserId] = useState("");
  const load = () => {
    const p = new URLSearchParams();
    if (docType) p.set("document_type", docType);
    if (userId) p.set("user_id", userId);
    api.get(`/admin/legal-acceptances?${p}`).then(({data}) => setRows(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [docType]);

  return (
    <div className="space-y-4" data-testid="admin-legal">
      <div className="flex flex-wrap gap-2">
        <select value={docType} onChange={e=>setDocType(e.target.value)} className="bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" data-testid="legal-doc-filter">
          <option value="">All documents</option>
          <option value="terms">Terms</option>
          <option value="privacy">Privacy</option>
          <option value="commercial_terms">Commercial Terms</option>
        </select>
        <input value={userId} onChange={e=>setUserId(e.target.value)} onKeyDown={e=>e.key==='Enter'&&load()} placeholder="Filter by user_id" className="bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm flex-1 min-w-[200px]" data-testid="legal-userid-filter" />
        <button onClick={load} className="btn-ghost-line text-xs inline-flex items-center gap-2" data-testid="legal-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5" /> Apply
        </button>
        <span className="text-xs text-ink-muted ml-auto self-center">{rows.length} records</span>
      </div>

      <div className="surface overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="border-b border-line">
            <tr className="text-left text-ink-muted uppercase tracking-widest">
              <th className="px-4 py-3 font-normal">When</th>
              <th className="px-4 py-3 font-normal">User</th>
              <th className="px-4 py-3 font-normal">Document</th>
              <th className="px-4 py-3 font-normal">Version</th>
              <th className="px-4 py-3 font-normal">IP</th>
              <th className="px-4 py-3 font-normal">User-Agent</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.map(r => (
              <tr key={r.id} data-testid={`legal-row-${r.id}`}>
                <td className="px-4 py-3 font-mono text-[10px] text-ink-muted">{formatDate(r.accepted_at)}</td>
                <td className="px-4 py-3 text-white">{r.user_email || r.user_id}</td>
                <td className="px-4 py-3 uppercase tracking-widest text-volt text-[10px]">{r.document_type}</td>
                <td className="px-4 py-3 font-mono">{r.document_version}</td>
                <td className="px-4 py-3 font-mono text-ink-muted">{r.ip_address || "—"}</td>
                <td className="px-4 py-3 text-ink-dim text-[10px] truncate max-w-[280px]">{r.user_agent || "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="6" className="px-4 py-8 text-center text-ink-muted">No records.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Verified brand directory ---------------- */
const EMPTY_VB = { brand_name: "", category: "", website: "", contact_email: "", instagram_handle: "", description: "", logo_url: "", is_active: true };

function BrandsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/verified-brands").then(({data}) => setItems(data));
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    if (form.id) await api.patch(`/admin/verified-brands/${form.id}`, form);
    else await api.post("/admin/verified-brands", form);
    setEditing(null); load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this verified brand?")) return;
    await api.delete(`/admin/verified-brands/${id}`);
    load();
  };

  return (
    <div className="space-y-4" data-testid="admin-brands">
      <div className="flex items-center">
        <span className="text-xs text-ink-muted">{items.length} verified brands</span>
        <button onClick={() => setEditing({ ...EMPTY_VB })} className="btn-volt text-xs inline-flex items-center gap-2 ml-auto" data-testid="vb-new-btn">
          <Plus className="w-3.5 h-3.5" /> Add brand
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map(b => (
          <div key={b.id} className="surface p-5" data-testid={`vb-${b.id}`}>
            <div className="flex justify-between items-start gap-3 mb-2">
              <div>
                <div className="font-heading text-lg uppercase tracking-tight">{b.brand_name}</div>
                <div className="text-xs text-ink-muted">{b.category}</div>
              </div>
              {!b.is_active && <span className="px-2 py-1 text-[10px] uppercase border border-line text-ink-muted">Inactive</span>}
            </div>
            {b.description && <p className="text-sm text-ink-muted mt-2 leading-relaxed line-clamp-2">{b.description}</p>}
            <div className="text-xs text-ink-dim mt-3 font-mono space-y-1">
              {b.website && <div>{b.website}</div>}
              {b.contact_email && <div>{b.contact_email}</div>}
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setEditing(b)} className="btn-ghost-line text-xs inline-flex items-center gap-1" data-testid={`vb-edit-${b.id}`}>
                <Edit3 className="w-3 h-3" /> Edit
              </button>
              <button onClick={() => del(b.id)} className="btn-ghost-line text-xs inline-flex items-center gap-1 hover:text-[#FF3B30]" data-testid={`vb-delete-${b.id}`}>
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="surface p-8 text-center text-ink-muted text-sm md:col-span-2">No verified brands yet. Add the first one to start curating the athlete directory.</div>}
      </div>

      {editing && <VBEditor initial={editing} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}

function VBEditor({ initial, onSave, onClose }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));
  const submit = async () => {
    setSaving(true); setErr("");
    try { await onSave(f); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="vb-editor">
      <div className="bg-bg-surface border border-volt w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-line flex justify-between items-center">
          <h2 className="font-heading text-xl uppercase">{f.id ? "Edit brand" : "Add verified brand"}</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-ink-muted hover:text-white" /></button>
        </div>
        <div className="p-6 space-y-4">
          <Input label="Brand name *" v={f.brand_name} on={v=>upd("brand_name",v)} testid="vb-name" />
          <Input label="Category" v={f.category} on={v=>upd("category",v)} testid="vb-category" />
          <Input label="Website" v={f.website} on={v=>upd("website",v)} testid="vb-website" />
          <Input label="Contact email" v={f.contact_email} on={v=>upd("contact_email",v)} testid="vb-email" />
          <Input label="Instagram handle" v={f.instagram_handle} on={v=>upd("instagram_handle",v)} testid="vb-ig" />
          <Input label="Logo URL" v={f.logo_url} on={v=>upd("logo_url",v)} testid="vb-logo" />
          <label className="block">
            <div className="label-eyebrow mb-2">Description</div>
            <textarea value={f.description} onChange={e=>upd("description", e.target.value)} rows={3} className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white resize-none" data-testid="vb-description" />
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={f.is_active} onChange={e=>upd("is_active", e.target.checked)} className="w-4 h-4 accent-volt" data-testid="vb-active" />
            <span className="text-sm text-white">Active <span className="text-ink-muted text-xs">(visible to athletes)</span></span>
          </label>
          {err && <div className="text-xs text-[#FF3B30]">{err}</div>}
        </div>
        <div className="px-6 py-4 border-t border-line flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost-line">Cancel</button>
          <button onClick={submit} disabled={saving || !f.brand_name} className="btn-volt disabled:opacity-40" data-testid="vb-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Shared bits ---------------- */
function Section({ title, children }) {
  return (
    <div className="surface p-6">
      <div className="label-eyebrow mb-4">{title}</div>
      {children}
    </div>
  );
}
function Stat({ label, value, accent, danger, testid }) {
  const cls = danger ? "border-[#FF3B30]/40 text-[#FF3B30]" : accent ? "border-volt/60 text-volt" : "border-line text-white";
  return (
    <div className={`border p-4 ${cls}`} data-testid={testid}>
      <div className="label-eyebrow text-[10px]">{label}</div>
      <div className="font-heading text-2xl mt-1">{value}</div>
    </div>
  );
}
function Input({ label, v, on, testid }) {
  return (
    <label className="block">
      <div className="label-eyebrow mb-2">{label}</div>
      <input value={v} onChange={e=>on(e.target.value)} className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white" data-testid={testid} />
    </label>
  );
}
function statusCls(s) {
  return ({
    queued:     "border-line text-ink-muted",
    sent:       "border-volt/60 text-volt",
    delivered:  "border-volt/60 text-volt",
    opened:     "border-white/60 text-white",
    clicked:    "border-white/60 text-white",
    bounced:    "border-[#FF3B30]/60 text-[#FF3B30]",
    complained: "border-[#FF3B30]/60 text-[#FF3B30]",
    failed:     "border-[#FF3B30]/60 text-[#FF3B30]",
  })[s] || "border-line text-ink-muted";
}
function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

// ============================================================
// PRICING TAB — live-edit Pro/Agency prices (creates new Stripe Price IDs)
// ============================================================
function PricingTab() {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({}); // {plan: draft}
  const [saving, setSaving] = useState(null); // current saving plan key
  const [msg, setMsg] = useState({ type: "", text: "" });

  const PLAN_KEYS = ["pro", "agency", "brand_lite", "brand_pro"];
  const PLAN_LABELS = { pro: "Pro", agency: "Agency", brand_lite: "Brand Lite", brand_pro: "Brand Pro" };

  const load = async () => {
    try {
      const { data } = await api.get("/admin/pricing");
      setData(data);
      const d = {};
      PLAN_KEYS.forEach(k => {
        // Defensive: if the backend ever returns a subset of plans (e.g. mid-migration),
        // skip the missing key instead of crashing on `{...undefined}` → blank screen.
        if (data.plans && data.plans[k]) d[k] = { ...data.plans[k] };
      });
      setDrafts(d);
    } catch (e) { setMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message }); }
  };
  useEffect(() => { load(); }, []);

  const save = async (plan) => {
    setSaving(plan); setMsg({ type: "", text: "" });
    try {
      const draft = drafts[plan];
      const payload = { [plan]: { amount: Number(draft.amount), currency: draft.currency, interval: draft.interval, label: draft.label } };
      const { data } = await api.patch("/admin/pricing", payload);
      setMsg({ type: "ok", text: `${PLAN_LABELS[plan]} price saved. New Stripe Price: ${data.plans[plan].stripe_price_id}` });
      await load();
    } catch (e) {
      setMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setSaving(null); }
  };

  if (!data || Object.keys(drafts).length === 0) return <div className="label-eyebrow" data-testid="pricing-loading">Loading pricing…</div>;

  const isDirty = (orig, draft) => orig.amount !== Number(draft.amount) || orig.currency !== draft.currency ||
    orig.interval !== draft.interval || orig.label !== draft.label;

  return (
    <div className="space-y-6" data-testid="admin-pricing">
      <div className="surface p-5 border-volt/30 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-volt flex-shrink-0 mt-0.5" />
        <div className="text-sm text-ink-muted">
          {data.note} A new Stripe Price object is created on save; the old one stays active so existing subscribers are unaffected.
        </div>
      </div>

      {msg.text && (
        <div className={`surface p-4 ${msg.type === "ok" ? "border-volt/50 text-volt" : "border-[#FF3B30]/50 text-[#FF3B30]"}`} data-testid="pricing-msg">
          {msg.text}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {PLAN_KEYS.filter(k => data.plans?.[k] && drafts[k]).map(k => (
          <PriceCard key={k}
            plan={k}
            orig={data.plans[k]}
            draft={drafts[k]}
            setDraft={(d) => setDrafts(prev => ({ ...prev, [k]: d }))}
            allowed={data}
            onSave={() => save(k)}
            saving={saving === k}
            dirty={isDirty(data.plans[k], drafts[k])}
          />
        ))}
      </div>
    </div>
  );
}

function PriceCard({ plan, orig, draft, setDraft, allowed, onSave, saving, dirty }) {
  const upd = (k, v) => setDraft({ ...draft, [k]: v });
  return (
    <div className="surface p-6" data-testid={`pricing-card-${plan}`}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="label-eyebrow">{plan === "pro" ? "Tier 1" : "Tier 2"}</div>
          <h3 className="font-heading text-2xl uppercase tracking-tight mt-1">{orig.label}</h3>
        </div>
        <div className="text-right">
          <div className="label-eyebrow">Current</div>
          <div className="font-heading text-2xl text-volt mt-1" data-testid={`pricing-current-${plan}`}>
            {currencySymbol(orig.currency)}{orig.amount.toFixed(2)}
            <span className="text-xs text-ink-muted ml-1">/ {orig.interval === "month" ? "mo" : "yr"}</span>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="label-eyebrow block mb-1.5">Amount</label>
            <input
              type="number" step="0.01" min="0.01" max="10000"
              value={draft.amount}
              onChange={(e) => upd("amount", e.target.value)}
              className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white font-mono"
              data-testid={`pricing-amount-${plan}`}
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Currency</label>
            <select
              value={draft.currency}
              onChange={(e) => upd("currency", e.target.value)}
              className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white uppercase"
              data-testid={`pricing-currency-${plan}`}
            >
              {allowed.allowed_currencies.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Interval</label>
            <select
              value={draft.interval}
              onChange={(e) => upd("interval", e.target.value)}
              className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white"
              data-testid={`pricing-interval-${plan}`}
            >
              {allowed.allowed_intervals.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="label-eyebrow block mb-1.5">Label (shown in Stripe product name)</label>
          <input
            value={draft.label}
            onChange={(e) => upd("label", e.target.value)}
            maxLength={40}
            className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white"
            data-testid={`pricing-label-${plan}`}
          />
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-line flex items-center justify-between gap-3">
        <div className="text-[10px] text-ink-muted font-mono truncate flex-1" title={orig.stripe_price_id}>
          {orig.stripe_price_id || "no Stripe price yet"}
        </div>
        <button
          onClick={onSave}
          disabled={!dirty || saving}
          className="btn-volt inline-flex items-center gap-2 text-xs disabled:opacity-40"
          data-testid={`pricing-save-${plan}`}
        >
          {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {saving ? "Saving…" : "Save price"}
        </button>
      </div>
    </div>
  );
}

function currencySymbol(c) {
  return { gbp: "£", usd: "$", eur: "€" }[c] || c.toUpperCase() + " ";
}

